import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  LinearProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

const TEST_CASE_COLUMNS_FALLBACK = [
  'Наименование',
  'Предусловия',
  'Шаги',
  'Постусловия',
  'Ожидаемый результат',
  'Тестовые данные',
];

const normalizeJob = (raw) => {
  if (!raw || typeof raw !== 'object') {
    return null;
  }
  const status = typeof raw.status === 'string' ? raw.status.toUpperCase() : 'PENDING';
  const completed = Boolean(raw.completed) || status === 'COMPLETE' || status === 'ERROR';
  const error = Boolean(raw.error) || status === 'ERROR';
  const progressValue = Number(raw.progress);
  const progress = Number.isFinite(progressValue)
    ? Math.max(0, Math.min(100, Math.round(progressValue)))
    : 0;

  return {
    ...raw,
    status,
    completed,
    error,
    progress,
    active: !completed && !error,
    message: typeof raw.message === 'string' ? raw.message : '',
  };
};

const buildEmptyRow = (columns) => {
  const row = {};
  columns.forEach((column) => {
    row[column] = '';
  });
  return row;
};

const normalizeRows = (rows, columns) => {
  if (!Array.isArray(rows)) {
    return [];
  }

  return rows
    .filter((row) => row && typeof row === 'object')
    .map((row) => {
      const normalized = {};
      columns.forEach((column) => {
        const value = row[column];
        normalized[column] = value == null ? '' : String(value);
      });
      return normalized;
    });
};

const extractFileName = (headers, fallback) => {
  const disposition = headers?.['content-disposition'];
  if (typeof disposition !== 'string') {
    return fallback;
  }
  const match = disposition.match(/filename\*=UTF-8''([^;]+)|filename="?([^";]+)"?/i);
  if (!match) {
    return fallback;
  }
  const encoded = match[1] || match[2];
  try {
    return decodeURIComponent(encoded);
  } catch (error) {
    return encoded || fallback;
  }
};

const downloadBlob = (blobData, filename) => {
  const blob = blobData instanceof Blob
    ? blobData
    : new Blob([blobData ?? new ArrayBuffer(0)]);
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

export default function StepThreeTestCasesPanel({
  projectId,
  apiClient,
  refreshSignal = 0,
  mode = 'manual',
  disabled = false,
  disabledReason = null,
}) {
  const isLoadMode = mode === 'load';
  const [testCaseJob, setTestCaseJob] = useState(null);
  const [testCaseArtifact, setTestCaseArtifact] = useState(null);
  const [testCaseColumns, setTestCaseColumns] = useState(TEST_CASE_COLUMNS_FALLBACK);
  const [testCaseRows, setTestCaseRows] = useState([]);
  const [testCaseLoading, setTestCaseLoading] = useState(false);
  const [testCaseError, setTestCaseError] = useState(null);
  const [testCaseSaving, setTestCaseSaving] = useState(false);
  const [testCaseDownloading, setTestCaseDownloading] = useState(false);

  const hasTestCaseArtifact = Boolean(testCaseArtifact?.id);

  const applyTestCaseArtifact = useCallback((artifact) => {
    const columns = Array.isArray(artifact?.columns) && artifact.columns.length > 0
      ? artifact.columns
      : TEST_CASE_COLUMNS_FALLBACK;
    setTestCaseColumns(columns);
    setTestCaseRows(normalizeRows(artifact?.rows, columns));
    setTestCaseArtifact(artifact || null);
  }, []);

  const loadTestCaseArtifactByJob = useCallback(async (jobId) => {
    if (!jobId) {
      return;
    }
    const response = await apiClient.get(`/api/projects/${projectId}/story-qa/test-cases/jobs/${jobId}/artifact`);
    applyTestCaseArtifact(response?.data ?? null);
  }, [apiClient, applyTestCaseArtifact, projectId]);

  const pollTestCaseJob = useCallback(async (jobId) => {
    try {
      const response = await apiClient.get(`/api/projects/${projectId}/story-qa/test-cases/jobs/${jobId}`);
      const normalized = normalizeJob(response?.data);
      if (!normalized) {
        return;
      }
      setTestCaseJob(normalized);
      if (normalized.completed && !normalized.error) {
        await loadTestCaseArtifactByJob(normalized.jobId);
      }
    } catch (error) {
      setTestCaseError(error?.response?.data?.error || error.message || 'Не удалось получить статус генерации тест-кейсов.');
    }
  }, [apiClient, loadTestCaseArtifactByJob, projectId]);

  const loadInitialState = useCallback(async () => {
    setTestCaseLoading(true);
    setTestCaseError(null);

    try {
      const [testCaseArtifactRes, testCaseActiveRes] = await Promise.allSettled([
        apiClient.get(`/api/projects/${projectId}/story-qa/test-cases/artifacts/latest`),
        apiClient.get(`/api/projects/${projectId}/story-qa/test-cases/jobs/active`),
      ]);

      if (testCaseArtifactRes.status === 'fulfilled') {
        applyTestCaseArtifact(testCaseArtifactRes.value?.data ?? null);
      }

      if (testCaseActiveRes.status === 'fulfilled') {
        setTestCaseJob(normalizeJob(testCaseActiveRes.value?.data));
      }
    } catch (error) {
      setTestCaseError(error?.response?.data?.error || error.message || 'Не удалось загрузить данные шага 3.');
    } finally {
      setTestCaseLoading(false);
    }
  }, [apiClient, applyTestCaseArtifact, projectId]);

  useEffect(() => {
    setTestCaseJob(null);
    setTestCaseArtifact(null);
    setTestCaseColumns(TEST_CASE_COLUMNS_FALLBACK);
    setTestCaseRows([]);
    loadInitialState();
  }, [loadInitialState, projectId]);

  useEffect(() => {
    if (refreshSignal > 0) loadInitialState();
  }, [loadInitialState, refreshSignal]);

  useEffect(() => {
    if (!testCaseJob?.active || !testCaseJob?.jobId) {
      return undefined;
    }

    const timer = setInterval(() => {
      pollTestCaseJob(testCaseJob.jobId);
    }, 2000);

    return () => clearInterval(timer);
  }, [pollTestCaseJob, testCaseJob?.active, testCaseJob?.jobId]);

  const handleGenerateTestCases = useCallback(async () => {
    if (disabled || testCaseJob?.active) {
      return;
    }

    setTestCaseError(null);
    try {
      const response = await apiClient.post(`/api/projects/${projectId}/story-qa/test-cases/generate`);
      const normalized = normalizeJob(response?.data);
      setTestCaseJob(normalized);
      if (normalized?.completed && !normalized.error) {
        await loadTestCaseArtifactByJob(normalized.jobId);
      }
    } catch (error) {
      setTestCaseError(error?.response?.data?.error || error.message || 'Не удалось запустить генерацию тест-кейсов.');
    }
  }, [apiClient, disabled, loadTestCaseArtifactByJob, projectId, testCaseJob?.active]);

  const handleAddTestCaseRow = useCallback(() => {
    setTestCaseRows((prev) => [...prev, buildEmptyRow(testCaseColumns)]);
  }, [testCaseColumns]);

  const handleRemoveTestCaseRow = useCallback((index) => {
    setTestCaseRows((prev) => prev.filter((_, rowIndex) => rowIndex !== index));
  }, []);

  const handleTestCaseCellChange = useCallback((index, column, value) => {
    setTestCaseRows((prev) => prev.map((row, rowIndex) => {
      if (rowIndex !== index) {
        return row;
      }
      return { ...row, [column]: value };
    }));
  }, []);

  const handleSaveTestCases = useCallback(async () => {
    if (!hasTestCaseArtifact || testCaseSaving) {
      return;
    }

    setTestCaseSaving(true);
    setTestCaseError(null);
    try {
      const response = await apiClient.put(
        `/api/projects/${projectId}/story-qa/test-cases/artifacts/${testCaseArtifact.id}`,
        { rows: testCaseRows }
      );
      applyTestCaseArtifact(response?.data ?? null);
    } catch (error) {
      setTestCaseError(error?.response?.data?.error || error.message || 'Не удалось сохранить тест-кейсы.');
    } finally {
      setTestCaseSaving(false);
    }
  }, [apiClient, applyTestCaseArtifact, hasTestCaseArtifact, projectId, testCaseArtifact?.id, testCaseRows, testCaseSaving]);

  const handleDownloadTestCases = useCallback(async () => {
    if (!hasTestCaseArtifact || testCaseDownloading) {
      return;
    }

    setTestCaseDownloading(true);
    setTestCaseError(null);
    try {
      const response = await apiClient.get(
        `/api/projects/${projectId}/story-qa/test-cases/artifacts/${testCaseArtifact.id}/download`,
        { responseType: 'blob' }
      );
      const filename = extractFileName(response?.headers, 'qa-test-cases.xlsx');
      downloadBlob(response?.data, filename);
    } catch (error) {
      setTestCaseError(error?.response?.data?.error || error.message || 'Не удалось скачать тест-кейсы.');
    } finally {
      setTestCaseDownloading(false);
    }
  }, [apiClient, hasTestCaseArtifact, projectId, testCaseArtifact?.id, testCaseDownloading]);

  const testCaseStatus = useMemo(() => {
    if (testCaseJob?.active) {
      return { label: 'В процессе', color: 'info' };
    }
    if (testCaseJob?.error) {
      return { label: 'Ошибка', color: 'error' };
    }
    if (hasTestCaseArtifact) {
      return { label: 'Готово', color: 'success' };
    }
    return { label: 'Не запущено', color: 'default' };
  }, [hasTestCaseArtifact, testCaseJob?.active, testCaseJob?.error]);

  return (
    <Paper className="surface-glow" sx={{ p: 2 }}>
      <Stack spacing={2}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={1}
          alignItems={{ xs: 'flex-start', md: 'center' }}
          justifyContent="space-between"
        >
          <Typography variant="h6">{isLoadMode ? 'Шаг 2. Нагрузочные тест-кейсы' : 'Шаг 3. QA тест-кейсы'}</Typography>
          <Chip
            size="small"
            label={`${isLoadMode ? 'Нагрузочные тест-кейсы' : 'QA тест-кейсы'}: ${testCaseStatus.label}`}
            color={testCaseStatus.color}
            variant="outlined"
          />
        </Stack>

        {disabled && (
          <Alert severity="info">
            {disabledReason || 'LLM-операции временно недоступны.'}
          </Alert>
        )}

        <Paper variant="outlined" sx={{ p: 2 }}>
          <Stack spacing={1.5}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="subtitle1">
                {isLoadMode ? 'Нагрузочные тест-кейсы (из требований шага 1)' : 'Тест-кейсы для QA (из требований шага 1)'}
              </Typography>
              <Button
                size="small"
                variant="outlined"
                onClick={handleGenerateTestCases}
                disabled={disabled || testCaseJob?.active || testCaseLoading}
              >
                {testCaseJob?.active ? 'Генерация…' : (isLoadMode ? 'Сгенерировать нагрузочные кейсы' : 'Сгенерировать')}
              </Button>
            </Stack>

            {testCaseLoading ? (
              <Stack direction="row" spacing={1} alignItems="center">
                <CircularProgress size={18} />
                <Typography variant="body2" color="text.secondary">Загрузка тест-кейсов…</Typography>
              </Stack>
            ) : null}

            <Typography variant="body2" color="text.secondary">
              Генерация включает позитивные и негативные сценарии с применением техник тест-дизайна.
            </Typography>

            {testCaseError && <Alert severity="error">{testCaseError}</Alert>}

            {testCaseJob?.active && (
              <Stack spacing={0.5}>
                <Typography variant="body2" color="text.secondary">
                  {testCaseJob.message || 'Генерация тест-кейсов в процессе…'}
                </Typography>
                <LinearProgress variant="determinate" value={testCaseJob.progress || 0} />
              </Stack>
            )}

            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="body2" color="text.secondary">
                Поля берутся из шаблона XLSX репозитория.
              </Typography>
              <Button size="small" onClick={handleAddTestCaseRow}>
                Добавить строку
              </Button>
            </Stack>

            <Paper variant="outlined" sx={{ overflowX: 'auto' }}>
              <Table size="small" sx={{ minWidth: 820 }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: 48 }}>#</TableCell>
                    {testCaseColumns.map((column) => (
                      <TableCell key={column}>{column}</TableCell>
                    ))}
                    <TableCell align="right" sx={{ width: 56 }}>Действия</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {testCaseRows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={testCaseColumns.length + 2}>
                        <Typography variant="body2" color="text.secondary">
                          Пока нет строк тест-кейсов.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : testCaseRows.map((row, index) => (
                    <TableRow key={`test-case-row-${index}`}>
                      <TableCell>{index + 1}</TableCell>
                      {testCaseColumns.map((column) => (
                        <TableCell key={`${column}-${index}`} sx={{ minWidth: 170 }}>
                          <TextField
                            value={row[column] || ''}
                            onChange={(event) => handleTestCaseCellChange(index, column, event.target.value)}
                            size="small"
                            multiline
                            minRows={2}
                            fullWidth
                          />
                        </TableCell>
                      ))}
                      <TableCell align="right">
                        <Tooltip title="Удалить строку">
                          <span>
                            <IconButton size="small" color="error" onClick={() => handleRemoveTestCaseRow(index)}>
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Paper>

            <Stack direction="row" spacing={1} justifyContent="flex-end">
              <Button
                size="small"
                variant="outlined"
                onClick={handleSaveTestCases}
                disabled={!hasTestCaseArtifact || testCaseSaving}
              >
                {testCaseSaving ? 'Сохранение…' : 'Сохранить'}
              </Button>
              <Button
                size="small"
                variant="contained"
                onClick={handleDownloadTestCases}
                disabled={!hasTestCaseArtifact || testCaseDownloading}
              >
                {testCaseDownloading ? 'Скачивание…' : 'Скачать XLSX'}
              </Button>
            </Stack>
          </Stack>
        </Paper>
      </Stack>
    </Paper>
  );
}
